import { Injectable, Injector } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Observable } from 'rxjs';

export interface TemplateDialogData {
    _id?: string;
    templateName?: string;
    templateType: string;
    content?: string;
    heading: string;
    update: boolean;
    subject?: string;
    templatePlaceholder: string;
}

@Injectable({
    providedIn: 'root'
})
export class TemplateService {

    constructor(private injector: Injector) { }

    /**
     * Opens the template creation/editing dialog with consistent configuration
     * @param templateType - Type of template (EMAIL_TEMPLATE or WELCOME_TEMPLATE)
     * @param title - Dialog title
     * @param isUpdate - Whether this is for updating an existing template
     * @param existingData - Existing template data for updates
     * @returns Observable with the dialog result
     */
    openTemplateDialog(
        templateType: string,
        title: string,
        isUpdate: boolean = false,
        existingData?: any
    ): Observable<any> {
        // Get MatDialog from injector to avoid circular dependency
        const dialog = this.injector.get(MatDialog);

        // Prepare sample data with dynamic variables based on template type
        const sampleData = this.getSampleTemplateData(templateType);

        // Merge with existing data if updating
        const dialogData: TemplateDialogData = {
            templateType,
            heading: title,
            templatePlaceholder: templateType === 'WELCOME_TEMPLATE' ? 'Welcome template name' : 'Email template name',
            update: isUpdate,
            ...sampleData,
            ...existingData
        };

        // Dynamically import the component to avoid circular dependency
        return new Observable(observer => {
            import('../dashboard/branding/create-update-template/create-update-template.component').then(module => {
                const CreateUpdateTemplateComponent = module.CreateUpdateTemplateComponent;

                const dialogRef = dialog.open(CreateUpdateTemplateComponent, {
                    width: '90vw',
                    maxWidth: '1000px',
                    height: '90vh',
                    maxHeight: '900px',
                    disableClose: true,
                    panelClass: 'create-update-template-dialog',
                    data: dialogData
                });

                dialogRef.afterClosed().subscribe(result => {
                    observer.next(result);
                    observer.complete();
                });
            }).catch(error => {
                observer.error(error);
            });
        });
    }

    /**
     * Gets sample template content based on template type
     * @param templateType - Type of template
     * @returns Sample template data
     */
    private getSampleTemplateData(templateType: string): any {
        switch (templateType) {
            case 'EMAIL_TEMPLATE':
                return {
                    templateName: 'Assessment Invitation',
                    subject: 'You\'re Invited: {{jobTitle}} Assessment at {{enterpriseName}}',
                    content: `<p>Dear <span style="color: #1a365d;">{{candidateName}}</span>,</p>
<p>Great news! You've been selected to take an assessment for the <span style="color: #1a365d;">{{jobTitle}}</span> position at <span style="color: #1a365d;">{{enterpriseName}}</span>.</p>
<p><strong>Assessment Details</strong></p>
<p>Position: <span style="color: #1a365d;">{{jobTitle}}</span></p>
<p>Assessment Duration: <span style="color: #1a365d;">{{duration}}</span> minutes</p>
<p>Company: <span style="color: #1a365d;">{{enterpriseName}}</span></p>
<p>To begin your assessment, please click the button below:</p>
<div style="text-align: left; margin: 20px 0;">
    {{assessmentLinkButton}}
</div>
<p>Make sure you have a stable internet connection and a quiet environment before starting.</p>
<p>If you have any questions, feel free to contact us at <a href="mailto:{{companyEmail}}">{{companyEmail}}</a> or call us at {{ORG_PHONE_NO}}.</p>
<p>Wishing you the best of luck!</p>
<p>Best regards,<br>
<strong>{{enterpriseName}} Team</strong></p>
<hr>
<p style="font-size: 12px; color: #666;">This is an automated message. Please do not reply to this email.</p>`
                };

            case 'WELCOME_TEMPLATE':
                return {
                    templateName: 'Welcome to {{enterpriseName}} Assessment',
                    content: `<p>Hello <span style="color: #1a365d;">{{candidateName}}</span>,</p>
<p>Welcome to the assessment for the <span style="color: #1a365d;">{{jobTitle}}</span> position at <span style="color: #1a365d;">{{enterpriseName}}</span>.</p>
<p><strong>Assessment Overview</strong></p>
<p>Position: <span style="color: #1a365d;">{{jobTitle}}</span></p>
<p>Company: <span style="color: #1a365d;">{{enterpriseName}}</span></p>
<p>Duration: <span style="color: #1a365d;">{{duration}}</span> minutes</p>
<p>This assessment will evaluate your technical skills and problem-solving abilities relevant to the <span style="color: #1a365d;">{{jobTitle}}</span> role.</p>
<p><strong>Before You Begin:</strong></p>
<ul>
    <li>Stable internet connection</li>
    <li>Quiet environment</li>
    <li>Set aside <span style="color: #1a365d;">{{duration}}</span> minutes</li>
</ul>
<p>Good luck!</p>
<p>Best regards,<br>
<strong>{{enterpriseName}} Assessment Team</strong></p>`
                };

            default:
                return {
                    templateName: 'New Template',
                    subject: '',
                    content: `
                        <p>Dear {{candidate_name}},</p>
                        
                        <p>This is a sample template. You can use these variables:</p>
                        
                        <div class="template-variables">
                            <ul>
                                <li>{{candidate_name}} - Candidate's name</li>
                                <li>{{company_name}} - Company name</li>
                                <li>{{enterprise_name}} - Enterprise name</li>
                                <li>{{job_title}} - Job title</li>
                                <li>{{link}} - Assessment link</li>
                                <li>{{company_phone}} - Company phone number</li>
                                <li>{{company_email}} - Company email address</li>
                                <li>{{duration}} - Assessment duration</li>
                                <li>{{TOKEN}} - Assessment token</li>
                            </ul>
                        </div>
                        
                        <p>Best regards,<br>Your Company Team</p>
                    `
                };
        }
    }

    /**
     * Gets available template variables for the given template type
     * @param templateType - Type of template
     * @returns Array of available variables
     */
    getTemplateVariables(templateType: string): string[] {
        // Use camelCase to match evaltrix-admin and provide a modern feel
        // The backend substitutePlaceholders function handles these via synonymMap
        const commonVariables = [
            '{{candidateName}}',
            '{{enterpriseName}}',
            '{{jobTitle}}',
            '{{inviteLink}}',
            '{{deadline}}',
            '{{duration}}',
            '{{assessmentLinkButton}}',
            '{{ORG_PHONE_NO}}',
            '{{companyEmail}}'
        ];
        return commonVariables;
    }

    /**
     * Returns variables with human-friendly descriptions for the given template type
     */
    getTemplateVariableDocs(templateType: string): Array<{ token: string; description: string; label: string; }> {
        const common: Array<{ token: string; description: string; label: string; }> = [
            { token: '{{candidateName}}', label: 'Candidate Name', description: "Candidate's full name" },
            { token: '{{enterpriseName}}', label: 'Company Name', description: 'Enterprise account name' },
            { token: '{{jobTitle}}', label: 'Job Title', description: 'Title of the position/role' },
            { token: '{{inviteLink}}', label: 'Direct URL', description: 'Link to start/view the assessment' },
            { token: '{{deadline}}', label: 'Due Date', description: 'Assessment expiration date' },
            { token: '{{duration}}', label: 'Duration', description: 'Assessment duration in minutes' },
            { token: '{{assessmentLinkButton}}', label: 'Action Button', description: 'Styled button for assessment link' },
            { token: '{{ORG_PHONE_NO}}', label: 'Contact Phone', description: 'Support contact phone number' },
            { token: '{{companyEmail}}', label: 'Support Email', description: 'Support contact email address' }
        ];

        return common;
    }
}
